import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useId } from 'react';

/* -------------------------------------------------------- autofill guard --- */

/**
 * Keeps password managers out of fields that are not credentials — ruling D-4.
 *
 * During the operator walkthrough LastPass filled the deliverable **Label**
 * field twice and the numeric **Credits** field on the admin adjustment form.
 * Both are unremarkable text inputs, and to a password manager an unremarkable
 * text input near a form looks like somewhere to put a username.
 *
 * `autocomplete="off"` alone does not do it: LastPass and 1Password both ignore
 * it on principle, because sites used to abuse it on login forms. Each wants
 * its own opt-out, so all three are set together.
 *
 * Applied by default in `TextField`, `TextAreaField` and `SelectField`. A field
 * that IS a credential passes its own `autoComplete` (`email`,
 * `current-password`, `new-password`, `organization`, `name`) and keeps the
 * correct semantics — those are exactly the fields a password manager should
 * fill, and turning it off there would be user-hostile.
 */
export const NO_AUTOFILL = {
  autoComplete: 'off',
  'data-lpignore': 'true',
  'data-1p-ignore': '',
} as const;

/** The autofill attributes for a field, given whatever the caller asked for. */
function autofillProps(autoComplete: string | undefined): Record<string, string> {
  // A caller that names a semantic token means it: leave the manager alone.
  if (autoComplete !== undefined && autoComplete !== 'off') return { autoComplete };
  return { ...NO_AUTOFILL };
}

/* ------------------------------------------------------------- disabled --- */

/**
 * **What a locked field looks like** - Craig's recheck of 24 September.
 *
 * View-as disabled the right controls and only two of them *looked* it: the
 * country box, which got these classes when it was given a disabled state, and
 * the buttons, which had their own. Every text box and text area took the
 * `disabled` attribute and carried on looking editable, so the page said
 * read-only in a line at the top and contradicted itself everywhere below. A
 * field that refuses typing without looking like it refuses typing reads as a
 * broken page, which is the failure P-4 was about in the first place.
 *
 * **One constant, so this cannot drift again.** The country box had the right
 * styling for a week purely because it was written last; nothing made the
 * others match it.
 *
 * `:disabled` matches a control disabled by an ancestor `fieldset` as well as
 * one disabled directly, so the project form's whole locked block picks these
 * up without a prop on every field.
 */
export const DISABLED_FIELD =
  'disabled:bg-subtle disabled:text-fg-muted disabled:ring-rule disabled:cursor-not-allowed';

/**
 * The same, for a checkbox.
 *
 * Different classes because a checkbox has no text of its own: `text-*` is the
 * tick colour and the box itself is the background and the border. Greying the
 * label too would be wrong - the words still have to be readable, because they
 * are what says which setting is locked.
 */
export const DISABLED_CHECKBOX =
  'disabled:bg-sunken disabled:border-rule disabled:text-fg-faint disabled:cursor-not-allowed';

/* ------------------------------------------------------------------ Button */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'onDark';

/*
 * Ruling B: the primary is the website's, a `blue` fill with `ground` text
 * (8.6:1), lighter on hover as the website's is. Focus rings are `blue-ink`,
 * because `blue` itself is 2.2:1 against the white around a button.
 *
 * `onDark` is the website's ghost button, for the one place a button sits on
 * the dark ground: Sign out in the top bar and the menu.
 */
const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary:
    'bg-blue text-ground hover:bg-blue-bright focus-visible:outline-blue-ink disabled:bg-sunken disabled:text-fg-muted',
  secondary:
    'bg-card text-fg ring-1 ring-inset ring-rule-strong hover:bg-subtle focus-visible:outline-blue-ink disabled:text-fg-faint',
  ghost: 'text-fg-2 hover:text-fg hover:bg-sunken focus-visible:outline-blue-ink',
  onDark:
    'bg-transparent text-ink ring-1 ring-inset ring-line-strong hover:bg-raise focus-visible:outline-blue',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  busy?: boolean;
}

export function Button({
  variant = 'primary',
  busy = false,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps): React.JSX.Element {
  return (
    <button
      {...rest}
      disabled={disabled === true || busy}
      aria-busy={busy || undefined}
      className={`inline-flex items-center justify-center gap-2 rounded-control px-4 py-2.5 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed ${BUTTON_STYLES[variant]} ${className}`}
    >
      {busy ? 'Working…' : children}
    </button>
  );
}

/* ------------------------------------------------------------------- Field */

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Plain-language guidance shown under the label. */
  hint?: string;
  error?: string | undefined;
  /**
   * A unit shown inside the field, e.g. "%" or "credits".
   *
   * Decoration only — `aria-hidden`, never focusable, and never part of the
   * value. A field whose units live only in its label is one a person has to
   * remember the units for while typing.
   */
  suffix?: string;
}

export function TextField({
  label,
  hint,
  error,
  id,
  className = '',
  autoComplete,
  suffix,
  ...rest
}: TextFieldProps): React.JSX.Element {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <label htmlFor={inputId} className="block text-sm font-medium text-fg">
        {label}
      </label>
      {hint ? (
        <p id={hintId} className="mt-1 text-sm text-fg-muted">
          {hint}
        </p>
      ) : null}
      <div className="relative">
        <input
          {...rest}
          {...autofillProps(autoComplete)}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={`mt-2 block w-full rounded-control border-0 py-2 pl-3 text-fg shadow-sm ring-1 ring-inset placeholder:text-fg-faint focus:ring-2 focus:ring-inset focus:ring-blue-ink sm:text-sm ${DISABLED_FIELD} ${
            suffix === undefined ? 'pr-3' : 'pr-16'
          } ${error ? 'ring-danger' : 'ring-rule-strong'}`}
        />
        {suffix === undefined ? null : (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-3 mt-2 flex items-center text-sm text-fg-muted"
          >
            {suffix}
          </span>
        )}
      </div>
      {error ? (
        <p id={errorId} className="mt-1.5 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/* --------------------------------------------------------------- TextArea */

export interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
  error?: string | undefined;
}

export function TextAreaField({
  label,
  hint,
  error,
  id,
  className = '',
  rows = 4,
  autoComplete,
  ...rest
}: TextAreaFieldProps): React.JSX.Element {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <label htmlFor={fieldId} className="block text-sm font-medium text-fg">
        {label}
      </label>
      {hint ? (
        <p id={hintId} className="mt-1 text-sm text-fg-muted">
          {hint}
        </p>
      ) : null}
      <textarea
        {...rest}
        {...autofillProps(autoComplete)}
        id={fieldId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={`mt-2 block w-full rounded-control border-0 px-3 py-2 text-fg shadow-sm ring-1 ring-inset placeholder:text-fg-faint focus:ring-2 focus:ring-inset focus:ring-blue-ink sm:text-sm ${DISABLED_FIELD} ${
          error ? 'ring-danger' : 'ring-rule-strong'
        }`}
      />
      {error ? (
        <p id={errorId} className="mt-1.5 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/* ----------------------------------------------------------------- Select */

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: string;
  error?: string | undefined;
  children: ReactNode;
}

/**
 * A labelled `<select>`, so that every form control in the portal goes through
 * a component that applies the autofill guard rather than each page
 * remembering to.
 */
export function SelectField({
  label,
  hint,
  error,
  id,
  className = '',
  autoComplete,
  children,
  ...rest
}: SelectFieldProps): React.JSX.Element {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <label htmlFor={fieldId} className="block text-sm font-medium text-fg">
        {label}
      </label>
      {hint ? (
        <p id={hintId} className="mt-1 text-sm text-fg-muted">
          {hint}
        </p>
      ) : null}
      <select
        {...rest}
        {...autofillProps(autoComplete)}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={`mt-2 block w-full rounded-control border-0 px-3 py-2 text-fg shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-blue-ink sm:text-sm ${DISABLED_FIELD} ${
          error ? 'ring-danger' : 'ring-rule-strong'
        }`}
      >
        {children}
      </select>
      {error ? (
        <p id={errorId} className="mt-1.5 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------- Alert */

export function Alert({
  tone = 'error',
  children,
}: {
  tone?: 'error' | 'success' | 'info' | 'warning';
  children: ReactNode;
}): React.JSX.Element {
  const tones = {
    error: 'bg-danger-soft text-danger ring-danger-line',
    success: 'bg-ok-soft text-ok ring-ok-line',
    info: 'bg-sunken text-fg-2 ring-rule',
    // Something the operator should notice and decide about, which is neither
    // a failure nor a neutral aside — the pricing drift notice (D-9 ruling 7).
    warning: 'bg-warn-soft text-warn ring-warn-line',
  } as const;

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`rounded-control px-4 py-3 text-sm ring-1 ring-inset ${tones[tone]}`}
    >
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------- Card */

export function Card({
  title,
  children,
  className = '',
}: {
  title?: string;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <section
      className={`rounded-panel bg-card p-6 shadow-sm ring-1 ring-rule ${className}`}
    >
      {title ? <h2 className="text-sm font-semibold text-fg-muted">{title}</h2> : null}
      {children}
    </section>
  );
}
